import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock, 
  Calendar,
  Filter,
  Download,
  Eye,
  Activity,
  Zap,
  AlertCircle
} from 'lucide-react';
import platformApi from '../services/platformApi';

interface AnalyticsData {
  totalCalls: number;
  totalRooms: number;
  activeRooms: number;
  totalParticipants: number;
  successRate: number;
  avgDuration: number;
}

interface DailyUsage {
  date: string;
  apiCalls: number;
  roomsCreated: number;
  participants: number;
}


export const AnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [metric, setMetric] = useState('calls');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [recentActivity, setRecentActivity] = useState<Array<{
    endpoint: string;
    method: string;
    status_code: number;
    timestamp: string;
    response_time_ms: number;
  }>>([]);
  const [topEndpoints, setTopEndpoints] = useState<Array<{
    endpoint: string;
    call_count: number;
    avg_response_time: number;
    error_rate: number;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 7;
        const data = await platformApi.getAnalytics(days);
        // Convert API response to match component interface
        // Note: Backend returns 'analytics' not 'summary', and 'successRate' not 'avgErrorRate'
        setAnalyticsData({
          totalCalls: data.analytics.totalCalls,
          totalRooms: data.analytics.totalRooms,
          activeRooms: data.analytics.activeRooms,
          totalParticipants: data.analytics.totalParticipants,
          successRate: data.analytics.successRate, // Already success rate, not error rate
          avgDuration: data.analytics.avgDuration
        });
        // Convert daily usage format - backend already returns correct format
        setDailyUsage(data.dailyUsage || []);
        // Set real activity and endpoints data
        setRecentActivity(data.recentActivity || []);
        setTopEndpoints(data.topEndpoints || []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [dateRange]);

  const getMetricValue = (data: DailyUsage) => {
    switch (metric) {
      case 'calls': return data.apiCalls;
      case 'rooms': return data.roomsCreated;
      case 'participants': return data.participants;
      default: return data.apiCalls;
    }
  };

  const getMaxValue = () => {
    if (dailyUsage.length === 0) return 100;
    return Math.max(...dailyUsage.map(getMetricValue), 1);
  };

  const getMetricLabel = () => {
    switch (metric) {
      case 'calls': return 'API Calls';
      case 'rooms': return 'Rooms Created';
      case 'participants': return 'Participants';
      default: return 'API Calls';
    }
  };

  // Helper function to format recent activity from API data
  const formatRecentActivity = (activities: typeof recentActivity) => {
    return activities.slice(0, 5).map(activity => {
      // Determine action description based on endpoint
      let action = '';
      
      if (activity.endpoint.includes('/rooms/create')) {
        action = 'Room created';
      } else if (activity.endpoint.includes('/tokens/generate')) {
        action = 'Token generated';
      } else if (activity.endpoint.includes('/rooms/join')) {
        action = 'Room joined';
      } else if (activity.endpoint.includes('/analytics')) {
        action = 'Analytics requested';
      } else if (activity.endpoint.includes('/rooms/delete')) {
        action = 'Room deleted';
      } else {
        action = `${activity.method} request`;
      }
      
      // Format timestamp to relative time
      const timeAgo = formatTimeAgo(activity.timestamp);
      const status = activity.status_code < 400 ? 'success' : 'error';
      
      return { 
        time: timeAgo, 
        action, 
        endpoint: activity.endpoint, 
        status 
      };
    });
  };
  
  // Helper function to format timestamps as relative time
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return past.toLocaleDateString();
  };

  const displayedRecentActivity = formatRecentActivity(recentActivity);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-light-text-secondary dark:text-dark-text-secondary">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Use real data or fallback
  const displayData = analyticsData || {
    totalCalls: 0,
    totalRooms: 0,
    activeRooms: 0,
    totalParticipants: 0,
    successRate: 100,
    avgDuration: 0
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary">
            Analytics Dashboard
          </h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
            Monitor your API usage, performance metrics, and user engagement
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors">
            <Download size={20} className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total API Calls
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.totalCalls.toLocaleString()}
              </p>
              <p className="text-xs text-success mt-1 flex items-center">
                <TrendingUp size={12} className="mr-1" />
                +12.5% from last month
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-primary-500" />
          </div>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total Rooms
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.totalRooms.toLocaleString()}
              </p>
              <p className="text-xs text-success mt-1 flex items-center">
                <TrendingUp size={12} className="mr-1" />
                +8.3% from last month
              </p>
            </div>
            <Clock className="w-8 h-8 text-info" />
          </div>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Active Rooms
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.activeRooms}
              </p>
              <p className="text-xs text-warning mt-1 flex items-center">
                <Activity size={12} className="mr-1" />
                Real-time
              </p>
            </div>
            <Eye className="w-8 h-8 text-warning" />
          </div>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Total Participants
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.totalParticipants.toLocaleString()}
              </p>
              <p className="text-xs text-success mt-1 flex items-center">
                <TrendingUp size={12} className="mr-1" />
                +15.7% from last month
              </p>
            </div>
            <Users className="w-8 h-8 text-success" />
          </div>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Success Rate
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.successRate}%
              </p>
              <p className="text-xs text-success mt-1 flex items-center">
                <Zap size={12} className="mr-1" />
                Excellent performance
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-success" />
          </div>
        </div>

        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary">
                Avg Duration
              </p>
              <p className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary mt-1">
                {displayData.avgDuration}m
              </p>
              <p className="text-xs text-info mt-1 flex items-center">
                <Clock size={12} className="mr-1" />
                Per session
              </p>
            </div>
            <Calendar className="w-8 h-8 text-info" />
          </div>
        </div>
      </div>

      {/* Usage Chart */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Usage Trends
            </h2>
            <div className="flex items-center space-x-3">
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="px-3 py-1 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded text-sm text-light-text-primary dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="calls">API Calls</option>
                <option value="rooms">Rooms Created</option>
                <option value="participants">Participants</option>
              </select>
              <Filter className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="h-64">
            {/* Simple Bar Chart */}
            <div className="flex items-end justify-between h-full space-x-2">
              {dailyUsage.length > 0 ? dailyUsage.map((data, index) => {
                const value = getMetricValue(data);
                const height = (value / getMaxValue()) * 100;
                return (
                  <div key={data.date} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-primary-500 rounded-t opacity-80 hover:opacity-100 transition-opacity cursor-pointer relative group"
                      style={{ height: `${height}%` }}
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-dark-bg-primary dark:bg-light-bg-primary text-dark-text-primary dark:text-light-text-primary text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                          {getMetricLabel()}: {value.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2 transform -rotate-45 origin-left">
                      {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center text-light-text-secondary dark:text-dark-text-secondary">
                  <p>No data available for the selected period</p>
                  <p className="text-sm mt-2">Start using the Video API to see analytics here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity & Top Endpoints */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
          <div className="p-6 border-b border-light-border dark:border-dark-border">
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Recent Activity
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-4">
                  <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Loading recent activity...</div>
                </div>
              ) : displayedRecentActivity.length > 0 ? (
                displayedRecentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-light-border dark:border-dark-border last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        activity.status === 'success' ? 'bg-success' : 'bg-error'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                          {activity.action}
                        </p>
                        <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                          {activity.endpoint}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
                      {activity.time}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    No recent activity
                  </p>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                    Start using the API to see activity here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Endpoints */}
        <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border">
          <div className="p-6 border-b border-light-border dark:border-dark-border">
            <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
              Top API Endpoints
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-4">
                  <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Loading top endpoints...</div>
                </div>
              ) : topEndpoints.length > 0 ? (
                (() => {
                  const maxCalls = Math.max(...topEndpoints.map(e => e.call_count), 1);
                  return topEndpoints.map((endpoint, index) => {
                    const percentage = (endpoint.call_count / maxCalls) * 100;
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                            {endpoint.endpoint}
                          </span>
                          <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                            {endpoint.call_count.toLocaleString()} calls
                          </span>
                        </div>
                        <div className="w-full bg-light-bg-quaternary dark:bg-dark-bg-quaternary rounded-full h-2">
                          <div 
                            className="h-2 bg-primary-500 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-light-text-secondary dark:text-dark-text-secondary">
                          <span>Avg: {endpoint.avg_response_time}ms</span>
                          <span>Error: {endpoint.error_rate}%</span>
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                    No endpoint data available
                  </p>
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                    Start using the API to see top endpoints here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};